import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent, CartUpdateEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}













(function () {
  'use strict';

  if (window.__aiCompleteLookV15Bound) return;
  window.__aiCompleteLookV15Bound = true;

  const ID            = 'complete_look';
  const ROOT_SEL      = `complete-look-slider-${ID}`;
  const CARDS_VISIBLE = 3;

  let _adding = false;

  // ─── CART HELPERS (idénticos al v13 que funcionaba) ────────
  async function addToCart(variantId) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] })
    });
    if (!res.ok) throw new Error('add_failed');
  }

  async function getCartJson() {
    const res = await fetch('/cart.js', { headers: { 'Accept': 'application/json' }, cache: 'no-store' });
    if (!res.ok) throw new Error('cart_json_failed');
    return await res.json();
  }

  function openDrawerIfPossible() {
    const drawer = document.querySelector('cart-drawer-component');
    if (drawer && typeof drawer.open === 'function') drawer.open();
  }

  function dispatchThemeCartUpdate(cart) {
    try {
      const evt = new CartUpdateEvent(cart, 'complete-look-v15', {
        itemCount: cart.item_count, source: 'complete-look-v15', sections: {}
      });
      document.dispatchEvent(evt);
    } catch (_) {}
  }

  // ─── SLIDER ────────────────────────────────────────────────
  function initSlider(root) {
    if (root._aiClAbort) root._aiClAbort.abort();
    root._aiClAbort = new AbortController();
    const signal = root._aiClAbort.signal;

    const track    = root.querySelector('[data-ai-cl-track]');
    const dotsWrap = root.querySelector('[data-ai-cl-dots]');
    const btnPrev  = root.querySelector('[data-ai-cl-prev]');
    const btnNext  = root.querySelector('[data-ai-cl-next]');
    if (!track) return;

    // Randomizar orden
    const cards = Array.from(track.children);
    for (let i = cards.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      track.appendChild(cards[j]);
      [cards[i], cards[j]] = [cards[j], cards[i]];
    }

    // Dots
    const pages = Math.ceil(cards.length / CARDS_VISIBLE);
    dotsWrap.innerHTML = '';
    const dots = Array.from({ length: pages }, (_, i) => {
      const d = document.createElement('div');
      d.className = `ai-cl__dot-${ID}`;
      if (i === 0) d.classList.add('active');
      dotsWrap.appendChild(d);
      return d;
    });

    let currentPage = 0;

    function getCardWidth() {
      const c = track.firstElementChild;
      return c ? c.offsetWidth + 10 : 0;
    }

    function scrollToPage(page) {
      currentPage = Math.max(0, Math.min(page, pages - 1));
      track.scrollTo({ left: currentPage * CARDS_VISIBLE * getCardWidth(), behavior: 'smooth' });
      updateUI();
    }

    function updateUI() {
      dots.forEach((d, i) => d.classList.toggle('active', i === currentPage));
      if (btnPrev) currentPage === 0
        ? btnPrev.setAttribute('data-hidden', '')
        : btnPrev.removeAttribute('data-hidden');
      if (btnNext) currentPage >= pages - 1
        ? btnNext.setAttribute('data-hidden', '')
        : btnNext.removeAttribute('data-hidden');
    }

    if (btnPrev) btnPrev.addEventListener('click', () => scrollToPage(currentPage - 1), { signal });
    if (btnNext) btnNext.addEventListener('click', () => scrollToPage(currentPage + 1), { signal });

    let scrollTimer;
    track.addEventListener('scroll', () => {
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(() => {
        const cw = getCardWidth();
        if (!cw) return;
        currentPage = Math.round(track.scrollLeft / (CARDS_VISIBLE * cw));
        currentPage = Math.max(0, Math.min(currentPage, pages - 1));
        updateUI();
      }, 80);
    }, { passive: true, signal });

    updateUI();
  }

  // ─── ADD TO CART ───────────────────────────────────────────
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-ai-cl-add]');
    if (!btn) return;

    if (_adding || btn._aiAdding) return;
    _adding = true;
    btn._aiAdding = true;

    const card = btn.closest('[data-ai-cl-card]');
    if (!card) { _adding = false; btn._aiAdding = false; return; }

    const variantInput = card.querySelector('[data-ai-cl-variant]');
    const variantId = variantInput?.value || null;
    if (!variantId) { _adding = false; btn._aiAdding = false; return; }

    const textEl  = btn.querySelector('[data-ai-cl-add-text]') || btn;
    const oldText = textEl.textContent;
    btn.disabled  = true;
    textEl.textContent = '...';

    try {
      await addToCart(variantId);
      textEl.textContent = '✅';

      // Idéntico al v13 que funcionaba
      openDrawerIfPossible();
      const cart = await getCartJson();
      dispatchThemeCartUpdate(cart);
      document.dispatchEvent(new CustomEvent('cart:update', {
        bubbles: true,
        detail: { source: 'complete-look-v15' }
      }));

      setTimeout(() => { textEl.textContent = oldText; }, 650);

    } catch (err) {
      console.warn('[CompleteLook v15] error:', err);
      textEl.textContent = 'Error';
      setTimeout(() => { textEl.textContent = oldText; }, 900);
    } finally {
      setTimeout(() => {
        btn.disabled  = false;
        btn._aiAdding = false;
        _adding       = false;
      }, 750);
    }
  });

  // ─── BOOT ──────────────────────────────────────────────────
  function boot() {
    document.querySelectorAll(ROOT_SEL).forEach(initSlider);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  ['cart:open', 'drawer:open', 'cart-drawer:open'].forEach(evt => {
    document.addEventListener(evt, () => {
      document.querySelectorAll(ROOT_SEL).forEach(initSlider);
    });
  });

  new MutationObserver(() => {
    document.querySelectorAll(`${ROOT_SEL}:not([data-ai-cl-init])`).forEach(root => {
      root.setAttribute('data-ai-cl-init', '1');
      initSlider(root);
    });
  }).observe(document.body, { childList: true, subtree: true });

})();